import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";

import { CountryFlag } from "../../components/CountryFlag";
import { CustomSelector } from "../../components/CustomSelector";
import { CustomTextField } from "../../components/CustomTextField";
import { Label } from "../../components/Label";
import {
  BILLING_DETAILS_QUERY_KEY,
  BILLING_QUERY_KEY,
  getBillingDetails,
  saveBillingDetails,
} from "../../services/billingService";

const FIELDS = [
  { name: "legal_name", label: "Nombre o razón social", required: true, placeholder: "Ej: Agencia Ejemplo, S.L. o Laura Pérez García" },
  { name: "tax_id", label: "NIF (DNI, NIE o CIF)", required: true, placeholder: "Ej: B12345678 o 12345678Z" },
  { name: "address_line1", label: "Dirección", required: true, placeholder: "Calle, número", wide: true },
  { name: "address_line2", label: "Dirección (línea 2)", placeholder: "Piso, puerta, oficina…", wide: true },
  { name: "postal_code", label: "Código postal", required: true, placeholder: "Ej: 46001" },
  { name: "city", label: "Ciudad", required: true, placeholder: "Ej: Valencia" },
  { name: "state", label: "Provincia", placeholder: "Ej: Valencia" },
];

// Fuera de España: NIF-IVA intracomunitario en la UE, identificador fiscal del país en el resto
const FOREIGN_TAX_ID = { label: "Identificador fiscal", placeholder: "Ej: FR40303265045" };

const EMPTY_DETAILS = Object.fromEntries([...FIELDS.map((field) => [field.name, ""]), ["country", "ES"]]);

const REQUIRED_FIELDS = [...FIELDS.filter((field) => field.required).map((field) => field.name), "country"];

const COUNTRY_CODES = [
  "ES", "AD", "PT", "FR", "IT", "DE", "AT", "BE", "BG", "CY", "CZ", "DK", "EE", "FI", "GR", "HR", "HU", "IE", "LT",
  "LU", "LV", "MT", "NL", "PL", "RO", "SE", "SI", "SK", "CH", "GB", "US", "MX", "AR", "CL", "CO", "PE", "UY",
];

const regionNames = new Intl.DisplayNames(["es"], { type: "region" });

const COUNTRY_OPTIONS = COUNTRY_CODES.map((code) => ({
  value: code,
  label: regionNames.of(code),
  icon: <CountryFlag code={code} />,
}));

// DRF field errors come as { field: ["message"] }; the rest as { code, detail }
const parseSaveError = (data) => {
  if (data?.code === "invalid_tax_id") return { fields: { tax_id: data.detail }, general: "" };
  if (data?.detail) return { fields: {}, general: data.detail };
  const fields = Object.fromEntries(
    Object.entries(data || {})
      .filter(([name]) => name in EMPTY_DETAILS)
      .map(([name, messages]) => [name, [].concat(messages).join(" ")])
  );
  return {
    fields,
    general: Object.keys(fields).length ? "" : "No se han podido guardar los datos de facturación. Inténtalo de nuevo.",
  };
};

/** Load, edit and save the agency fiscal data; shared by the settings section and the pre-payment modal. */
export function useBillingDetailsForm() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: BILLING_DETAILS_QUERY_KEY,
    queryFn: getBillingDetails,
  });
  const [values, setValues] = useState(EMPTY_DETAILS);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setValues(data);
  }, [data]);

  const setField = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const dirty = Boolean(data) && Object.keys(EMPTY_DETAILS).some((name) => data[name] !== values[name]);

  // Resolves the saved details, or null if something is missing or the backend rejects them
  const submit = async () => {
    const missing = REQUIRED_FIELDS.filter((name) => !values[name].trim());
    if (missing.length) {
      setErrors(Object.fromEntries(missing.map((name) => [name, "Campo obligatorio"])));
      return null;
    }
    setSaving(true);
    setGeneralError("");
    try {
      const saved = await saveBillingDetails(values);
      queryClient.setQueryData(BILLING_DETAILS_QUERY_KEY, saved);
      // The backend only saves complete details
      queryClient.setQueryData(BILLING_QUERY_KEY, (summary) => summary && { ...summary, billing_details_complete: true });
      return saved;
    } catch (requestError) {
      const parsed = parseSaveError(requestError?.response?.data);
      setErrors(parsed.fields);
      setGeneralError(parsed.general);
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { values, errors, generalError, saving, dirty, isLoading, isError, refetch, setField, submit };
}

export const BillingDetailsFields = ({ form, disabled }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
    {FIELDS.map((baseField) => {
      const field = baseField.name === "tax_id" && form.values.country !== "ES" ? { ...baseField, ...FOREIGN_TAX_ID } : baseField;
      return (
        <Box key={field.name} sx={{ gridColumn: field.wide ? { sm: "1 / -1" } : undefined, minWidth: 0 }}>
          <Label sx={{ fontSize: "0.875rem" }}>
            {field.label}
            {field.required && " *"}
          </Label>
          <CustomTextField
            value={form.values[field.name]}
            onChange={(event) => form.setField(field.name, event.target.value)}
            placeholder={field.placeholder}
            sx={{ marginTop: "8px" }}
            error={Boolean(form.errors[field.name])}
            errorMessage={form.errors[field.name]}
            disabled={disabled || form.saving}
          />
        </Box>
      );
    })}
    <Box sx={{ minWidth: 0 }}>
      <Label sx={{ fontSize: "0.875rem" }}>País *</Label>
      <Box sx={{ mt: 1 }}>
        <CustomSelector
          value={form.values.country}
          onChange={(value) => form.setField("country", value)}
          options={COUNTRY_OPTIONS}
          leadingIcon={<CountryFlag code={form.values.country} />}
          searchable
          disabled={disabled || form.saving}
        />
      </Box>
    </Box>
  </Box>
);

BillingDetailsFields.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    saving: PropTypes.bool,
    setField: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
};
